import React, { useContext } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import FormatCurrency from 'components/Common/FormatCurrency'
import SolidExclamationCircleIcon from 'components/Luxkit/Icons/solid/SolidExclamationCircleIcon'
import Pill from 'components/Luxkit/Pill'

import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import GeoContext from 'contexts/geoContext'
import Group from 'components/utils/Group'
import TextButton from 'components/Luxkit/Button/TextButton'
import PaletteProvider from 'contexts/PaletteProvider'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Spacing from 'components/Common/Spacing'
import { ValueWrapper } from 'components/Common/OfferPricing/styles'
import useCheapestOfTourV2Offer from 'hooks/TourV2/useCheapestOfTourV2Offer'
import useTourV2PurchasableOptionDetails from 'hooks/TourV2/useTourV2PurchasableOptionDetails'
import useTourV2VariationDetails from 'hooks/TourV2/useTourV2VariationDetails'

export const StyledExclamationIcon = styled(SolidExclamationCircleIcon)`
  position: relative;
  margin-left: ${rem(5)};
  top: ${rem(3)};
`

interface Props {
  offer: Tours.V2Offer | Tours.V2OfferSummary;
  className?: string;
}

function TourV2PriceDetails(props: Props) {
  const { offer } = props
  const { showTaxExclusivePrices } = useContext(GeoContext)

  const {
    cheapestPurchasableOption,
    cheapestVariation,
  } = useCheapestOfTourV2Offer(offer)

  const {
    durationInDaysCount,
  } = useTourV2VariationDetails(cheapestVariation)

  const {
    pricePerPerson,
    roomTypeName,
    valuedAt,
  } = useTourV2PurchasableOptionDetails(cheapestPurchasableOption) || {}

  return (
    <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
      <Group direction="vertical">
        <PaletteProvider type="default">
        </PaletteProvider>
        <Spacing mt={4}>
          <Caption variant="large" colour="neutral-three">{durationInDaysCount} from</Caption>
        </Spacing>
        <Heading variant="heading4">
          {pricePerPerson && <FormatCurrency value={pricePerPerson} format="roundedDollar" />}
          <Caption variant="large" as="span" colour="neutral-two">/person</Caption>
        </Heading>
        {valuedAt &&
          <ValueWrapper variant="large" weight="bold" colour="neutral-one">
            <span data-testid="value-box">
              Valued up to <FormatCurrency value={Math.ceil(valuedAt.price)} />
            </span>
            <Pill kind="primary" variant="success">-{valuedAt.savingPercentage}%</Pill>
          </ValueWrapper>
          }
        <Caption variant="medium" colour={showTaxExclusivePrices ? 'highlight' : 'neutral-three'}>Includes taxes & fees</Caption>
        <Caption variant="medium" colour="neutral-three">
          {roomTypeName}
        </Caption>
      </Group>
      <CSSBreakpoint min="md">
        <TextButton kind="primary" nonInteractable>
          View
        </TextButton>
      </CSSBreakpoint>
    </Group>
  )
}

export default TourV2PriceDetails
